import React, { useState, useEffect } from "react";
import useChatStore from "../../store/chatStore";
import PresenceDot from "./PresenceDot";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import useAuthStore from "../../store/authStore";

function formatTimestamp(iso) {
  if (!iso) return "";
  const d       = new Date(iso);
  const now     = new Date();
  const diffMs  = now - d;
  const diffDay = Math.floor(diffMs / 86_400_000);
  if (diffDay === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7)  return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function getInitials(name = "") {
  return (
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

const TYPE_ICON = {
  GROUP:        "👥",
  SELF:         "📝",
  DEPARTMENT:   "🏢",
  PROJECT:      "📁",
  ANNOUNCEMENT: "📢",
};

const GRADIENTS = [
  "from-violet-600 to-blue-600",
  "from-blue-600 to-cyan-500",
  "from-emerald-600 to-teal-500",
  "from-rose-600 to-pink-500",
  "from-amber-500 to-orange-500",
  "from-indigo-600 to-purple-600",
];

const gradient = (id) => GRADIENTS[id % GRADIENTS.length];

// ── Name resolution ───────────────────────────────────────────────────────────
// Handles both shapes from the API:
//   Flat  (MemberMiniSerializer): { employee_id, full_name, role, is_active }
//   Nested (ConversationMemberSerializer): { user: { employee_id, full_name } }
function resolveDisplayName(conversation, currentUserId) {
  const { conv_type, name, members = [] } = conversation;
  if (conv_type === "SELF") return name || "Notes (You)";
  if (conv_type !== "DIRECT") return name || conv_type;

  const other = members.find((m) => {
    const id = m.employee_id ?? m.user?.employee_id ?? m.user_info?.employee_id;
    return id && String(id) !== String(currentUserId);
  });
  if (!other) return name || "Direct Message";
  return (
    other.full_name ??
    other.user?.full_name ??
    other.user_info?.full_name ??
    other.employee_id ??
    "Direct Message"
  );
}

function resolveOtherEid(conversation, currentUserId) {
  if (conversation.conv_type === "SELF") return currentUserId;
  if (conversation.conv_type !== "DIRECT") return null;
  const other = (conversation.members ?? []).find((m) => {
    const id = m.employee_id ?? m.user?.employee_id ?? m.user_info?.employee_id;
    return id && String(id) !== String(currentUserId);
  });
  return other?.employee_id ?? other?.user?.employee_id ?? other?.user_info?.employee_id ?? null;
}

function resolveAvatarUrl(conversation, currentUserId, myPhoto) {
  if (conversation.avatar_url) return resolvePhotoUrl(conversation.avatar_url);
  
  if (conversation.conv_type === "SELF") {
    return resolvePhotoUrl(myPhoto);
  }
  
  if (conversation.conv_type === "DIRECT") {
    const other = (conversation.members ?? []).find((m) => {
      const id = m.employee_id ?? m.user?.employee_id ?? m.user_info?.employee_id;
      return id && String(id) !== String(currentUserId);
    });
    const rawPhoto =
      other?.profile_photo ||
      other?.photo ||
      other?.user?.profile_photo ||
      other?.user?.photo ||
      other?.user_info?.profile_photo ||
      other?.user_info?.photo ||
      other?.avatar ||
      other?.user?.avatar ||
      other?.user_info?.avatar ||
      null;
    return resolvePhotoUrl(rawPhoto);
  }
  return null;
}

function lastMsgPreview(lastMsg, convType, currentUserId) {
  if (!lastMsg)              return "No messages yet";
  if (lastMsg.is_deleted)    return "Message deleted";

  let body = "";
  if (lastMsg.msg_type === "POLL" || (lastMsg.content && lastMsg.content.includes("poll_id") && lastMsg.content.includes("question"))) {
    try {
      const parsed = JSON.parse(lastMsg.decryptedContent || lastMsg.content || "{}");
      if (parsed.question) {
        body = `${parsed.question} (Poll)`;
      } else {
        body = "📊 (Poll)";
      }
    } catch {
      body = "📊 (Poll)";
    }
  } else if (lastMsg.msg_type === "POLL_VOTE" || (lastMsg.content && lastMsg.content.includes("poll_id") && lastMsg.content.includes("option_index"))) {
    body = "🗳️ Voted on poll";
  } else if (lastMsg.msg_type === "STICKER") {
    body = lastMsg.content && lastMsg.content.includes("giphy") ? "🖼️ GIF" : "🖼️ Sticker";
  } else {
    const text = (lastMsg.content ?? "").trim();
    if (text.startsWith("data:image/") || text.includes("media.giphy.com")) {
      body = text.includes("giphy") ? "🖼️ GIF" : "🖼️ Sticker";
    } else {
      body = text || (lastMsg.ciphertext ? "[encrypted]" : "No messages yet");
    }
  }

  if (convType !== "DIRECT") {
    const senderEid = lastMsg.sender?.employee_id ?? lastMsg.sender_id;
    const isOwn     = senderEid === currentUserId;
    const first     = isOwn
      ? "You"
      : (lastMsg.sender?.full_name ?? lastMsg.sender_name ?? "Someone").split(" ")[0];
    return `${first}: ${body}`;
  }
  return body;
}

// ── User Avatar Helper Component ───────────────────────────────────────────────

function UserAvatar({ photo, name, typeIcon, initials }) {
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setImgErr(false);
  }, [photo]);

  if (photo && !imgErr) {
    return (
      <img
        src={photo}
        alt={name}
        className="w-full h-full rounded-full object-cover"
        onError={() => setImgErr(true)}
      />
    );
  }
  if (typeIcon) {
    return <span className="text-base">{typeIcon}</span>;
  }
  return <>{initials}</>;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function ConversationItem({ conversation, currentUserId }) {
  const selectedConvId     = useChatStore((s) => s.selectedConvId);
  const selectConversation = useChatStore((s) => s.selectConversation);
  const clearUnread        = useChatStore((s) => s.clearUnread);
  
  // Resolve currentUserId robustly to avoid race conditions
  const currentUserIdFromStore = useChatStore((s) => s.currentUserId);
  const resolvedUserId = currentUserId || currentUserIdFromStore;

  // ✅ fallback to c.unread_count if store is empty on first load
  const unread             = useChatStore((s) => s.unreadCounts[conversation.id] ?? conversation.unread_count ?? 0);

  const myPhoto     = useAuthStore((s) => s.profilePhoto);
  const isSelected  = selectedConvId === conversation.id;
  const isDirect    = conversation.conv_type === "DIRECT";
  const displayName = resolveDisplayName(conversation, resolvedUserId);
  const otherEid    = resolveOtherEid(conversation, resolvedUserId);
  const avatarUrl   = resolveAvatarUrl(conversation, resolvedUserId, myPhoto);
  const lastMsg     = conversation.last_message;
  const preview     = lastMsgPreview(lastMsg, conversation.conv_type, resolvedUserId);
  const time        = lastMsg?.created_at ? formatTimestamp(lastMsg.created_at) : "";
  const initials    = getInitials(displayName);
  const grad        = gradient(conversation.id);
  const typeIcon    = TYPE_ICON[conversation.conv_type];

  const handleClick = () => {
    selectConversation(conversation.id);
    if (unread > 0) clearUnread(conversation.id);
  };

  return (
    <button
      onClick={handleClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left
        transition-all duration-150
        ${
          isSelected
            ? "bg-blue-600/20 border border-blue-500/30"
            : "hover:bg-white/5 border border-transparent hover:border-white/5"
        }
      `}
    >
      {/* Avatar */}
      <div className="relative shrink-0">
        <div
          className={`w-10 h-10 rounded-full bg-gradient-to-br ${grad} flex items-center justify-center text-white text-sm font-semibold select-none shadow-md overflow-hidden`}
        >
          <UserAvatar
            photo={avatarUrl}
            name={displayName}
            typeIcon={typeIcon}
            initials={initials}
          />
        </div>
        {isDirect && otherEid && (
          <div className="absolute -bottom-0.5 -right-0.5">
            <PresenceDot employeeId={otherEid} size="sm" />
          </div>
        )}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span
            className={`text-sm font-medium truncate ${
              isSelected ? "text-blue-200" : "text-slate-200"
            }`}
          >
            {displayName}
          </span>
          <span className="text-[10px] text-slate-500 shrink-0">{time}</span>
        </div>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <span className="text-xs text-slate-500 truncate leading-relaxed">
            {preview}
          </span>
          {unread > 0 && (
            <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}